import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AdminQcRunSummary } from '../../../interfaces/qcIssue.interface';
import { getWorkflowQcRunSummaries } from '../../../services/qcReview';
import { SUCCESS_GREEN, ERROR_RED, WARN_ORANGE, FIRST_PASS_PURPLE } from '../../Assessments/Workspace/qcTheme';

// Engineering view of a workflow's QC runs: provenance, dispatch stats, draft outcomes, per-question verdicts.

const Shell = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  color: 'rgba(0,0,0,0.87)',
});

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '18px 24px 14px',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
});

const Title = styled('h2')({
  margin: 0,
  fontSize: '1.15rem',
  fontWeight: 700,
});

const Subject = styled('span')({
  fontSize: '0.85rem',
  color: 'rgba(0,0,0,0.55)',
});

const CloseButton = styled('button')({
  marginLeft: 'auto',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: '50%',
  display: 'inline-flex',
  color: 'rgba(0,0,0,0.55)',
  '&:hover': { background: 'rgba(0,0,0,0.06)', color: 'rgba(0,0,0,0.8)' },
});

const Scroll = styled('div')({
  flex: 1,
  // minHeight 0 lets the flex child scroll; contain stops wheel scroll bleeding to the page behind.
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: '16px 24px 24px',
});

const StateLine = styled('p')({
  margin: '24px 0',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.6)',
});

const RunSection = styled('section')({
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: '4px',
  marginBottom: '16px',
  overflow: 'hidden',
});

// Accordion header: the whole row toggles its run open/closed.
const RunHead = styled('button')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '10px',
  padding: '12px 16px',
  background: '#f3f5fb',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { background: '#edf0f9' },
});

const Chevron = styled(ExpandMoreIcon, {
  shouldForwardProp: prop => prop !== 'up',
})<{ up: boolean }>(({ up }) => ({
  fontSize: 20,
  color: 'rgba(0,0,0,0.5)',
  transition: 'transform 0.15s ease',
  transform: up ? 'rotate(180deg)' : 'none',
}));

const RunChip = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: 'neutral' | 'good' | 'bad' | 'purple' }>(({ tone }) => {
  const colors = {
    neutral: { fg: '#455a64', bg: 'rgba(69,90,100,0.1)' },
    good: { fg: SUCCESS_GREEN, bg: 'rgba(46,125,50,0.1)' },
    bad: { fg: ERROR_RED, bg: 'rgba(198,40,40,0.1)' },
    purple: { fg: FIRST_PASS_PURPLE, bg: 'rgba(94,53,177,0.1)' },
  }[tone];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    height: '22px',
    padding: '0 10px',
    borderRadius: '11px',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: colors.fg,
    background: colors.bg,
  };
});

const RunMeta = styled('span')({
  fontSize: '0.78rem',
  color: 'rgba(0,0,0,0.55)',
  marginLeft: 'auto',
});

const StatGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: '10px 18px',
  padding: '14px 16px',
});

const Stat = styled('div')({
  minWidth: 0,
});

const StatLabel = styled('div')({
  fontSize: '0.65rem',
  fontWeight: 600,
  letterSpacing: '0.8px',
  textTransform: 'uppercase',
  color: 'rgba(0,0,0,0.5)',
});

const StatValue = styled('div')({
  fontSize: '0.9rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.85)',
  overflowWrap: 'break-word',
});

const QuestionTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8rem',
  '& th': {
    textAlign: 'left',
    padding: '8px 16px',
    fontSize: '0.65rem',
    fontWeight: 600,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: 'rgba(0,0,0,0.5)',
    borderTop: '1px solid rgba(0,0,0,0.08)',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    background: '#fafbfe',
  },
  '& td': {
    padding: '7px 16px',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    color: 'rgba(0,0,0,0.75)',
  },
});

const VerdictText = styled('span', {
  shouldForwardProp: prop => prop !== 'verdict',
})<{ verdict: string }>(({ verdict }) => ({
  fontWeight: 600,
  color: verdict === 'agree' ? SUCCESS_GREEN
    : verdict === 'disagree' ? ERROR_RED
      : verdict === 'uncertain' ? '#ed6c02' : '#616161',
}));

const InjectionFlag = styled('span')({
  color: ERROR_RED,
  fontWeight: 700,
});

// Click-to-sort table headers (Moore, 2026-08-27): toggle asc/desc, no filters.
const SortHeader = styled('button')({
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  textTransform: 'inherit',
  letterSpacing: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
});

const SortArrow = styled('span')({
  fontSize: '0.6rem',
  lineHeight: 1,
});

type SortDir = 'asc' | 'desc';

function sortRows<T>(rows: T[], field: string | null, dir: SortDir): T[] {
  if (!field) return rows;
  const value = (row: T): number | string | null => {
    const raw = (row as Record<string, unknown>)[field];
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'boolean') return Number(raw);
    if (typeof raw === 'number') return raw;
    return String(raw);
  };
  return [...rows].sort((a, b) => {
    const av = value(a);
    const bv = value(b);
    if (av === null) return 1;
    if (bv === null) return -1;
    const cmp = typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv));
    return dir === 'asc' ? cmp : -cmp;
  });
}

const RECOMMENDATION_COLORS: Record<string, string> = {
  approve: SUCCESS_GREEN,
  needs_update: WARN_ORANGE,
};

const RecommendationText = styled('span', {
  shouldForwardProp: prop => prop !== 'recommendation',
})<{ recommendation: string | null }>(({ recommendation }) => ({
  fontWeight: 700,
  color: recommendation ? RECOMMENDATION_COLORS[recommendation] ?? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.45)',
}));

const CommentCell = styled('td')({
  whiteSpace: 'normal',
  maxWidth: '560px',
  lineHeight: 1.45,
});

const RUN_TYPE_LABELS: Record<string, string> = {
  assessment: 'Initial Run',
  qc_recheck: 'Recheck',
  remediation: 'Remediation',
};

function formatRunDate(iso: string): string {
  const date = new Date(iso);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

// Rounds are the admin's mental model — raw QCRun ids are global and gappy, so they ride as fine print only.
function runLabel(run: AdminQcRunSummary): string {
  if (run.reviewType === 'qc_recheck') {
    const round = run.reevaluations[0]?.roundNumber;
    return round ? `Round ${round} Recheck` : 'Recheck';
  }
  return RUN_TYPE_LABELS[run.reviewType] || run.reviewType;
}

export interface QcRunDetailsModalProps {
  open: boolean;
  workflowId?: number;
  subject: string;
  onClose: () => void;
}

export default function QcRunDetailsModal({ open, workflowId, subject, onClose }: QcRunDetailsModalProps): JSX.Element {
  const [runs, setRuns] = useState<AdminQcRunSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!open || !workflowId) return;
    setLoaded(false);
    setError(false);
    getWorkflowQcRunSummaries(workflowId)
      .then(data => {
        setRuns(data);
        // Newest run open, the rest collapsed.
        setExpanded(data.length ? { [data[0].id]: true } : {});
        setLoaded(true);
      })
      .catch(() => { setError(true); setLoaded(true); });
  }, [open, workflowId]);

  const toggleRun = (runId: number) => setExpanded(prev => ({ ...prev, [runId]: !prev[runId] }));

  // Sort state per run table, keyed "<runId>:questions" / "<runId>:reevaluations".
  const [sorts, setSorts] = useState<Record<string, { field: string; dir: SortDir }>>({});
  const toggleSort = (key: string, field: string) => setSorts(prev => {
    const current = prev[key];
    const dir: SortDir = current?.field === field && current.dir === 'asc' ? 'desc' : 'asc';
    return { ...prev, [key]: { field, dir } };
  });
  const sortHeader = (key: string, field: string, label: string) => {
    const current = sorts[key];
    return (
      <SortHeader type='button' onClick={() => toggleSort(key, field)}>
        {label}
        {current?.field === field && <SortArrow>{current.dir === 'asc' ? '▲' : '▼'}</SortArrow>}
      </SortHeader>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label='QC run details'
      PaperProps={{ sx: { width: '96vw', maxWidth: 1400, height: '92vh', borderRadius: '4px', display: 'flex', flexDirection: 'column' } }}
      maxWidth={false}
    >
      <Shell>
        <Header>
          <Title>QC Run Details</Title>
          <Subject>{subject}</Subject>
          <CloseButton type='button' aria-label='Close QC run details' onClick={onClose}>
            <CloseIcon sx={{ fontSize: 22 }} />
          </CloseButton>
        </Header>
        <Scroll>
          {!loaded && <StateLine>Loading runs…</StateLine>}
          {loaded && error && <StateLine>Could not load QC runs for this workflow.</StateLine>}
          {loaded && !error && runs.length === 0 && <StateLine>No QC runs yet for this workflow.</StateLine>}
          {loaded && !error && runs.map(run => (
            <RunSection key={run.id}>
              <RunHead type='button' aria-expanded={!!expanded[run.id]} onClick={() => toggleRun(run.id)}>
                <RunChip tone='purple'>{runLabel(run)}</RunChip>
                <RunChip tone={run.status === 'complete' ? 'good' : run.status === 'failed' ? 'bad' : 'neutral'}>
                  {run.status}
                </RunChip>
                {run.mode !== 'production' && <RunChip tone='neutral'>{run.mode}</RunChip>}
                <RunMeta>{run.triggeredBy} · {formatRunDate(run.createdAt)} · run {run.id}</RunMeta>
                <Chevron up={!!expanded[run.id]} />
              </RunHead>
              {expanded[run.id] && (<>
              <StatGrid>
                <Stat><StatLabel>Model</StatLabel><StatValue>{run.modelVersion}</StatValue></Stat>
                <Stat><StatLabel>Prompt</StatLabel><StatValue>{run.promptVersion}</StatValue></Stat>
                <Stat>
                  <StatLabel>Guidance</StatLabel>
                  <StatValue>{run.guidanceVersion !== null ? `v${run.guidanceVersion}` : 'None'}</StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Progress</StatLabel>
                  <StatValue>{run.completedCount} / {run.dispatchedCount}</StatValue>
                </Stat>
                <Stat><StatLabel>Flagged</StatLabel><StatValue>{run.flaggedCount}</StatValue></Stat>
                <Stat><StatLabel>Escalated</StatLabel><StatValue>{run.escalatedCount}</StatValue></Stat>
                <Stat>
                  <StatLabel>Drafts</StatLabel>
                  <StatValue>
                    {run.draftsCreated} ({run.draftsAccepted} accepted · {run.draftsRejected} rejected · {run.draftsPending} pending)
                  </StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Avg draft rating</StatLabel>
                  <StatValue>{run.avgDraftRating !== null ? `${run.avgDraftRating} / 5` : '—'}</StatValue>
                </Stat>
              </StatGrid>
              {run.questions.length > 0 && (
                <QuestionTable>
                  <thead>
                    <tr>
                      <th>{sortHeader(`${run.id}:questions`, 'shortCode', 'Question')}</th>
                      <th>{sortHeader(`${run.id}:questions`, 'verdict', 'Verdict')}</th>
                      <th>{sortHeader(`${run.id}:questions`, 'confidence', 'Confidence')}</th>
                      <th>{sortHeader(`${run.id}:questions`, 'errorClass', 'Error class')}</th>
                      <th>{sortHeader(`${run.id}:questions`, 'suspectedInjection', 'Injection')}</th>
                      <th>{sortHeader(`${run.id}:questions`, 'spellingNoteCount', 'Spelling')}</th>
                      <th>{sortHeader(`${run.id}:questions`, 'crossReferenceCount', 'Refs')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortRows(
                      run.questions,
                      sorts[`${run.id}:questions`]?.field ?? null,
                      sorts[`${run.id}:questions`]?.dir ?? 'asc',
                    ).map(question => (
                      <tr key={question.questionId}>
                        <td>{question.shortCode || question.questionId}</td>
                        <td><VerdictText verdict={question.verdict}>{question.verdict.replace(/_/g, ' ')}</VerdictText></td>
                        <td>{question.confidence !== null ? `${Math.round(question.confidence * 100)}%` : '—'}</td>
                        <td>{question.errorClass && question.errorClass !== 'none' ? question.errorClass.replace(/_/g, ' ') : '—'}</td>
                        <td>{question.suspectedInjection ? <InjectionFlag>suspected</InjectionFlag> : '—'}</td>
                        <td>{question.spellingNoteCount || '—'}</td>
                        <td>{question.crossReferenceCount || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </QuestionTable>
              )}
              {run.reevaluations.length > 0 && (
                <QuestionTable>
                  <thead>
                    <tr>
                      <th>{sortHeader(`${run.id}:reevaluations`, 'shortCode', 'Question')}</th>
                      <th>{sortHeader(`${run.id}:reevaluations`, 'roundNumber', 'Round')}</th>
                      <th>{sortHeader(`${run.id}:reevaluations`, 'recommendation', 'Recommendation')}</th>
                      <th>{sortHeader(`${run.id}:reevaluations`, 'certainty', 'Certainty')}</th>
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortRows(
                      run.reevaluations,
                      sorts[`${run.id}:reevaluations`]?.field ?? null,
                      sorts[`${run.id}:reevaluations`]?.dir ?? 'asc',
                    ).map(reevaluation => (
                      <tr key={`${reevaluation.issueId}-${reevaluation.roundNumber}`}>
                        <td>{reevaluation.shortCode || reevaluation.questionId}</td>
                        <td>{reevaluation.roundNumber}</td>
                        <td>
                          <RecommendationText recommendation={reevaluation.recommendation}>
                            {reevaluation.recommendation ? reevaluation.recommendation.replace(/_/g, ' ') : 'degraded'}
                          </RecommendationText>
                        </td>
                        <td>{reevaluation.certainty !== null ? `${Math.round(reevaluation.certainty * 100)}%` : '—'}</td>
                        <CommentCell>{reevaluation.comment}</CommentCell>
                      </tr>
                    ))}
                  </tbody>
                </QuestionTable>
              )}
              </>)}
            </RunSection>
          ))}
        </Scroll>
      </Shell>
    </Dialog>
  );
}
